import { Injectable } from '@angular/core';
import {
    GatewayResponse,
    MaterialToSupplierDeleteRequest,
    MaterialToSupplierEditRequest,
    MaterialToSupplierGetRequest,
    MaterialToSupplierGetRequestByMaterialId,
} from '@dave/types';
import { MaterialToSupplier,MaterialToSupplierAddRequest } from '@dave/types/dist/proto/erp/materialToSupplier';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { firstValueFrom,ReplaySubject,Subject } from 'rxjs';
import { MaterialToSupplierEntity,MaterialToSupplierEntityFromBackend } from '../entities/materialToSupplier.entity';
import { State } from '../State';
import { BaseActionTypes } from '../State/actions/base.actions';
import { AppGatewayService } from './app-gateway.service';

enum ErrorCodes {
    Add = 'MaterialToSupplier Hinzufügen fehlgeschlagen',
    Load = 'MaterialToSupplier Abrufen fehlgeschlagen',
    Modify = 'MaterialToSupplier Bearbeiten fehlgeschlagen',
    Remove = 'MaterialToSupplier Löschen fehlgeschlagen',
}

@Injectable({
    providedIn: 'root',
})
export class MaterialToSupplierDataService {
    private requestByMaterialIdCache = new Map<number, Subject<MaterialToSupplierEntity[]>>();
    private requestBySupplierIdCache = new Map<number, Subject<MaterialToSupplierEntity[]>>();
    private requestMaterialIdsBySupplierIdCache = new Map<number, Subject<number[]>>();
    private requestByMaterialIdsCache= new Map<string, Subject<MaterialToSupplierEntity[]>>();

    constructor(private store$: Store<State>, private gatewayService: AppGatewayService, private actions$: Actions) {}

    add(addRequest: MaterialToSupplierAddRequest) {
        return this.gatewayService
            .Request({ ErpMaterialToSupplierAdd: addRequest })
            .then((res) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    if (this.requestMaterialIdsBySupplierIdCache.has(+addRequest.SupplierId)) {
                        const cache$ = this.requestMaterialIdsBySupplierIdCache.get(+addRequest.SupplierId);
                        firstValueFrom(cache$).then((cache) => {
                            cache$.next([...cache, +addRequest.MaterialId]);
                        });
                    }
                    return this.pullIfNecessary(res.ErpMaterialToSupplierAdd.MaterialToSupplier);
                } else {
                    throw res.Errors;
                }
            })
            .catch((err) => {
                this.store$.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.Add,
                            Err: err,
                        },
                    }),
                );
            });
    }
    change(request: MaterialToSupplierEditRequest) {
        return this.gatewayService
            .Request({ ErpMaterialToSupplierEdit: request })
            .then((res) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    return this.pullIfNecessary(res.ErpMaterialToSupplierEdit.MaterialToSupplier);
                } else {
                    throw res.Errors;
                }
            })
            .catch((err) => {
                this.store$.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.Modify,
                            Err: err,
                        },
                    }),
                );
            });
    }
    delete(request: MaterialToSupplierDeleteRequest) {
        return this.gatewayService
            .Request({ ErpMaterialToSupplierDelete: request })
            .then((res) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    if (this.requestMaterialIdsBySupplierIdCache.has(+request.SupplierId)) {
                        const cache$ = this.requestMaterialIdsBySupplierIdCache.get(+request.SupplierId);
                        firstValueFrom(cache$).then((cache) => {
                            cache$.next(cache.filter((mId) => mId !== +request.MaterialId));
                        });
                    }
                    return this.pullIfNecessary(res.ErpMaterialToSupplierDelete.MaterialToSupplier);
                } else {
                    throw res.Errors;
                }
            })
            .catch((err) => {
                this.store$.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.Remove,
                            Err: err,
                        },
                    }),
                );
            });
    }
    getMaterialToSupplierByMaterialId$(materialId: number, forceReload = false) {
        if (forceReload || !this.requestByMaterialIdCache.has(materialId)) {
            this.requestByMaterialIdCache.set(materialId, new ReplaySubject<MaterialToSupplierEntity[]>(1));
            this.getRequestWithDescription(materialId).then((response) => {
                this.requestByMaterialIdCache.get(materialId).next(response);
            });
        }
        return this.requestByMaterialIdCache.get(materialId);
    }

    getMaterialToSupplierByMaterialIds$(materialIds: number[]) {
        if (!this.requestByMaterialIdsCache.has(materialIds.join('-'))) {
            this.requestByMaterialIdsCache.set(materialIds.join('-'), new ReplaySubject<MaterialToSupplierEntity[]>(1));
            this.getRequest(null, materialIds).then((response) => {
                this.requestByMaterialIdsCache.get(materialIds.join('-')).next(response);
            });
        }
        return this.requestByMaterialIdsCache.get(materialIds.join('-'));
    }

    private async pullIfNecessary(m2s: MaterialToSupplier) {
        if (m2s.MaterialId && this.requestByMaterialIdCache.has(+m2s.MaterialId)) {
            const response = await this.getRequestWithDescription(+m2s.MaterialId);
            this.requestByMaterialIdCache.get(+m2s.MaterialId).next(response);
        }
        if (m2s.SupplierId && this.requestBySupplierIdCache.has(+m2s.SupplierId)) {
            const response = await this.getRequest( +m2s.SupplierId);
            this.requestBySupplierIdCache.get(+m2s.SupplierId).next(response);
        }
        this.requestByMaterialIdsCache.forEach(async (v, k) => {
            if (k.split('-').includes(m2s.MaterialId.toString())) {
                const response = await this.getRequest(
                    null,
                    k.split('-').map((id) => Number(id)),
                );
                this.requestByMaterialIdsCache.get(k).next(response);
            }
        });
    }
    private getRequestWithDescription(materialId: number): Promise<MaterialToSupplierEntity[]> {
        const requestPayload: Omit<MaterialToSupplierGetRequestByMaterialId, 'Page'> = {
            WithDeleted: false,
            MaterialId: materialId && materialId + '',
        };
        const requests$ = new Promise<{ ErpMaterialToSupplierGetByMaterialId: Partial<GatewayResponse['ErpMaterialToSupplierGetByMaterialId']>; Errors: GatewayResponse['Errors'] }>(async (resolve, reject) => {
            const results: GatewayResponse['ErpMaterialToSupplierGetByMaterialId']['MaterialToSupplier'] = [];
            let errors = {};

            const get = async () => {
                const res = await this.gatewayService.Request({
                    ErpMaterialToSupplierGetByMaterialId: {
                        ...requestPayload,
                    },
                });
                if (res.ErpMaterialToSupplierGetByMaterialId?.MaterialToSupplier) {
                    results.push(...res.ErpMaterialToSupplierGetByMaterialId.MaterialToSupplier);
                }
                return res;
            };

                await get().catch((err) => console.warn('load page error', err));

            resolve({ ErpMaterialToSupplierGetByMaterialId: { MaterialToSupplier: results }, Errors: errors });
        });
        return requests$
            .then((res) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    return res.ErpMaterialToSupplierGetByMaterialId.MaterialToSupplier.map(MaterialToSupplierEntityFromBackend);
                } else {
                    throw res.Errors;
                }
            })
            .catch((err) => {
                this.store$.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.Load,
                            Err: err,
                        },
                    }),
                );
                return [];
            });
    }

    private getRequest(supplierId?: number, materialIds?: number[]): Promise<MaterialToSupplierEntity[]> {
        const requestPayload: Omit<MaterialToSupplierGetRequest, 'Page'> = {
            WithDeleted: false,
            PageSize: 20000,
            MaterialIds: materialIds && materialIds.map(id => id.toString()),
            SupplierId: supplierId && supplierId + '',
        };



        const requests$ = new Promise<{ ErpMaterialToSupplierGet: Partial<GatewayResponse['ErpMaterialToSupplierGet']>; Errors: GatewayResponse['Errors'] }>(async (resolve, reject) => {
            let page = 1;
            const results: GatewayResponse['ErpMaterialToSupplierGet']['MaterialToSuppliers'] = [];
            let errors = {};
            let lastPageReached = false;

            const getPage = async (page: number) => {
                const res = await this.gatewayService.Request({
                    ErpMaterialToSupplierGet: {
                        ...requestPayload,
                        Page: page,
                    },
                });
                if (res.ErpMaterialToSupplierGet?.MaterialToSuppliers) {
                    results.push(...res.ErpMaterialToSupplierGet.MaterialToSuppliers);
                }
                if (res.Errors) {
                    errors = { ...errors, ...res.Errors };
                }
                if (page >= res.ErpMaterialToSupplierGet.PageCount) {
                    lastPageReached = true;
                }
                return res;
            };
            while (!lastPageReached) {
                await getPage(page).catch((err) => console.warn('load page error', err));
                page++;
            }
            resolve({ ErpMaterialToSupplierGet: { MaterialToSuppliers: results }, Errors: errors });
        });
        return requests$
            .then((res) => {
                if (Object.keys(res?.Errors || {}).length === 0) {
                    return res.ErpMaterialToSupplierGet.MaterialToSuppliers.map(MaterialToSupplierEntityFromBackend);
                } else {
                    throw res.Errors;
                }
            })
            .catch((err) => {
                this.store$.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.Load,
                            Err: err,
                        },
                    }),
                );
                return [];
            });
    }
}
